import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import ShareJobModal from "@/components/ShareJobModal";
import InlineSignupModal from "@/components/InlineSignupModal";
import useSeo from "@/lib/useSeo";
import SeoMeta from "@/components/SeoMeta";
import { toast } from "sonner";
import {
    ArrowLeft,
    ArrowRight,
    Briefcase,
    Buildings,
    MapPin,
    UserCircle,
    CurrencyInr,
    ShareNetwork,
    CalendarBlank,
    Lightning,
    Heart,
} from "@phosphor-icons/react";

/**
 * Public JD detail page — shareable, indexable, and mobile-first.
 * Sits at /jobs/:id. The backend counterpart /api/share/j/:id serves
 * the crawler-facing OG preview HTML.
 */
export default function PublicJobDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [params] = useSearchParams();
    // Batch 3 (Feb 2026) — referral code from the sharer, kept through the
    // signup deep-link so bounty attribution survives the funnel.
    const refCode = (params.get("ref") || "").trim().toUpperCase().slice(0, 12) || null;

    // V2.0.59 — Share Analytics landing event. If the visitor arrived via
    // a share link (has utm_source / share_id / fbclid), record the `land`
    // event and stash share_id in sessionStorage so the eventual apply
    // form can echo it back as the `apply` event and complete the funnel.
    useEffect(() => {
        if (!id) return;
        const shareId = params.get("share_id") || null;
        const utmSource = params.get("utm_source") || null;
        const utmMedium = params.get("utm_medium") || null;
        const utmCampaign = params.get("utm_campaign") || null;
        const fbclid = params.get("fbclid") || null;
        // Only record if there's actually a share signal — plain organic
        // page views must NOT hit the analytics collection.
        if (!shareId && !utmSource && !fbclid) return;
        try {
            if (shareId) sessionStorage.setItem(`share_attr:${id}`, JSON.stringify({
                share_id: shareId, channel: utmSource || "unknown", ts: Date.now(),
            }));
        } catch { /* private mode — silent */ }
        api.post("/share/events", {
            event: "land",
            job_id: id,
            channel: utmSource || "unknown",
            share_id: shareId,
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_campaign: utmCampaign,
            fbclid,
        }).catch(() => {}); // never break the page
    }, [id, params]);
    const [job, setJob] = useState(null);
    const [err, setErr] = useState("");
    const [shareOpen, setShareOpen] = useState(false);
    // Candidate's OWN referral code (for sharing further, if signed in).
    const [myReferralCode, setMyReferralCode] = useState(null);
    // V1.6.7 — Save Job (wishlist). `saved` toggles the heart state.
    // Anon users → InlineSignupModal on click. After signup, we retry
    // the save via the pending action queued in localStorage.
    const [saved, setSaved] = useState(false);
    const [saveBusy, setSaveBusy] = useState(false);
    const [signupModal, setSignupModal] = useState(false);

    // SEO — Google Jobs vertical search indexes JobPosting JSON-LD, so a
    // properly-shaped structured-data blob per job dramatically boosts
    // organic discovery. Title / description / canonical also set here.
    // V1.5.8.48 — SEO metadata now managed centrally by
    // <SeoMeta endpoint={`/seo/job/${id}/meta`} /> below. The backend
    // meta has richer fields (baseSalary in INR, validThrough, dynamic
    // OG image URL, directApply flag, canonical career-page URL) that
    // the previous inline useSeo() didn't supply, and running both
    // simultaneously produced duplicate/conflicting <head> tags
    // (testing agent report 128 flagged this).

    useEffect(() => {
        let mounted = true;
        api
            .get(`/public/jobs/${id}`)
            .then((r) => mounted && setJob(r.data))
            .catch((e) => mounted && setErr(e.response?.data?.detail || "This role is no longer accepting applications."));
        return () => { mounted = false; };
    }, [id]);

    // If a signed-in candidate opens this JD, fetch their referral code so the
    // Share button can bake it into the outgoing link.
    useEffect(() => {
        if (user?.role !== "candidate") return;
        let mounted = true;
        api
            .get("/me/candidate/referrals")
            .then((r) => mounted && setMyReferralCode(r.data?.referral_code || null))
            .catch(() => {});
        return () => { mounted = false; };
    }, [user]);

    // Reflect the JD in the browser tab as a nice-to-have (crawlers use backend OG)
    useEffect(() => {
        if (job?.title) document.title = `${job.title}${job.client?.name ? " — " + job.client.name : ""} | NaukriDaddy`;
        return () => { document.title = "NaukriDaddy — India's smartest agency ATS"; };
    }, [job]);

    // V1.6.7 — Fetch this candidate's saved-jobs list so the heart
    // reflects current state. Runs only for authed candidates.
    useEffect(() => {
        if (user?.role !== "candidate") return;
        let mounted = true;
        api
            .get("/me/candidate/saved-jobs")
            .then((r) => {
                if (mounted) {
                    const ids = r.data?.job_ids || [];
                    setSaved(ids.includes(id));
                }
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, [user, id]);

    // V1.6.7 — Retry a pending save after signup. If the anon user hit
    // Save and then completed signup, `pending_save_job` in
    // localStorage triggers the save now that they're authed.
    useEffect(() => {
        if (user?.role !== "candidate") return;
        const pending = localStorage.getItem("pending_save_job");
        if (pending === id) {
            localStorage.removeItem("pending_save_job");
            doSave();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, id]);

    const doSave = async () => {
        setSaveBusy(true);
        try {
            await api.post(`/me/candidate/saved-jobs/${id}`);
            setSaved(true);
            toast.success("Saved to your wishlist.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save.");
        } finally { setSaveBusy(false); }
    };

    const doUnsave = async () => {
        setSaveBusy(true);
        try {
            await api.delete(`/me/candidate/saved-jobs/${id}`);
            setSaved(false);
            toast.success("Removed from wishlist.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't unsave.");
        } finally { setSaveBusy(false); }
    };

    const handleSaveClick = () => {
        if (user?.role === "candidate") {
            saved ? doUnsave() : doSave();
        } else {
            // Anon or non-candidate → open inline signup, persist intent.
            localStorage.setItem("pending_save_job", id);
            setSignupModal(true);
        }
    };

    // V1.6.6 — Progressive access: always route to /apply/{id}, which is
    // guest-safe (public /public/jobs/{id}/apply endpoint). The apply page
    // itself handles referral funneling. We no longer force signup as a
    // pre-condition to seeing / filling the application form.
    const applyHref = `/apply/${id}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ""}`;

    if (err) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 text-center">
                <div className="max-w-md">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">Not available</p>
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-ink mb-3">
                        {err}
                    </h1>
                    <Link
                        to="/jobs"
                        data-testid="jd-back-to-board"
                        className="btn-gradient inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider"
                    >
                        Browse all roles <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-[#525252]">
                <SeoMeta endpoint={`/seo/job/${id}/meta`} />
                Loading role…
            </div>
        );
    }

    // Batch 3 (Feb 2026) — role closed/paused/inactive. Show a friendly panel
    // with up to 5 similar OPEN jobs instead of a hard 404.
    if (job.status_flag === "closed") {
        return (
            <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="jd-closed-page">
                <header className="bg-ink text-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <Brand variant="light" size="h-7 sm:h-8" to={null} />
                        </Link>
                        <Link
                            to="/jobs"
                            className="text-xs text-white/70 hover:text-white font-bold uppercase tracking-wider"
                        >
                            All roles →
                        </Link>
                    </div>
                </header>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Role no longer active
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
                        {job.title || "This role was closed"}
                    </h1>
                    <p className="text-[#525252] mb-8">
                        Applications for this position are no longer open —
                        but here are a few active roles that look similar.
                    </p>
                    {(job.similar_jobs || []).length === 0 ? (
                        <div className="bg-white border border-[#E5E5E5] p-6 text-sm text-[#525252]">
                            No similar open roles right now. Head to the{" "}
                            <Link to="/jobs" className="text-cobalt font-bold">
                                full board
                            </Link>{" "}
                            or set up a profile and we&apos;ll ping you when a match appears.
                        </div>
                    ) : (
                        <div
                            data-testid="jd-similar-jobs"
                            className="grid sm:grid-cols-2 gap-3"
                        >
                            {job.similar_jobs.map((s) => (
                                <Link
                                    key={s.id}
                                    to={`/jobs/${s.id}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ""}`}
                                    data-testid={`jd-similar-${s.id}`}
                                    className="bg-white border border-[#E5E5E5] hover:border-ink p-5 transition-colors"
                                >
                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-1">
                                        {s.client?.name || s.department || "General"}
                                    </p>
                                    <h3 className="font-heading text-lg font-bold tracking-tight mb-2">
                                        {s.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 text-[11px] text-[#525252]">
                                        {s.location && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin size={10} weight="bold" /> {s.location}
                                            </span>
                                        )}
                                        {s.salary_range && (
                                            <span className="font-bold text-ink">
                                                · {s.salary_range}
                                            </span>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const meta = [
        (job.display_location || job.location) && { Icon: MapPin, text: job.display_location || job.location },
        job.employment_type && { Icon: Briefcase, text: job.employment_type },
        job.seniority && { Icon: UserCircle, text: job.seniority },
        job.salary_range && { Icon: CurrencyInr, text: job.salary_range },
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="public-jd-page">
            <SeoMeta endpoint={`/seo/job/${id}/meta`} />
            {/* Header */}
            <header className="bg-ink text-white sticky top-0 z-30">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2 shrink-0">
                        <Brand variant="light" size="h-7 sm:h-8" to={null} />
                    </Link>
                    <div className="hidden sm:flex items-center gap-3 text-xs">
                        <Link
                            to="/jobs"
                            className="text-white/70 hover:text-white font-bold uppercase tracking-wider"
                        >
                            All roles
                        </Link>
                        {user?.role === "candidate" ? (
                            <Link
                                to="/candidate"
                                className="bg-cobalt hover:bg-cobalt-dark px-3 py-1.5 font-bold uppercase tracking-wider transition-colors"
                            >
                                My portal
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/candidate/login"
                                    data-testid="jd-candidate-login"
                                    className="text-white/70 hover:text-white font-bold uppercase tracking-wider"
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to="/candidate/signup"
                                    data-testid="jd-candidate-signup"
                                    className="bg-cobalt hover:bg-cobalt-dark px-3 py-1.5 font-bold uppercase tracking-wider transition-colors"
                                >
                                    Sign up
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                <Link
                    to="/jobs"
                    data-testid="jd-back"
                    className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] hover:text-ink inline-flex items-center gap-1 mb-4"
                >
                    <ArrowLeft size={12} weight="bold" /> All roles
                </Link>

                {/* Title block */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                    <div className="min-w-0">
                        {job.client?.name && (
                            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-cobalt mb-2 inline-flex items-center gap-1.5">
                                <Buildings size={11} weight="bold" />
                                {job.client.name}
                                {job.client.industry && (
                                    <span className="text-[#A3A3A3] font-normal">
                                        · {job.client.industry}
                                    </span>
                                )}
                            </p>
                        )}
                        <h1
                            data-testid="jd-title"
                            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter break-words"
                        >
                            {job.title}
                        </h1>
                        {meta.length > 0 && (
                            <div className="flex flex-wrap gap-3 mt-4 text-xs text-[#525252]">
                                {meta.map(({ Icon, text }, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1.5">
                                        <Icon size={12} weight="bold" /> {text}
                                    </span>
                                ))}
                                {job.created_at && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <CalendarBlank size={12} weight="bold" />
                                        {new Date(job.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2 self-start sm:self-auto shrink-0">
                        <button
                            onClick={() => setShareOpen(true)}
                            data-testid="jd-share-button"
                            className="border border-[#E5E5E5] hover:border-ink px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors bg-white"
                        >
                            <ShareNetwork size={12} weight="bold" /> Share
                        </button>
                        <button
                            onClick={handleSaveClick}
                            disabled={saveBusy}
                            data-testid="jd-save-button"
                            aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
                            className={`border px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors disabled:opacity-60 ${saved ? "bg-red-50 border-red-300 text-red-600 hover:border-red-500" : "border-[#E5E5E5] hover:border-ink bg-white text-ink"}`}
                        >
                            <Heart size={12} weight={saved ? "fill" : "bold"} />
                            {saved ? "Saved" : "Save"}
                        </button>
                        <button
                            onClick={() => navigate(applyHref)}
                            data-testid="jd-apply-button"
                            className="btn-gradient px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                        >
                            <Lightning size={12} weight="bold" /> Apply now
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                    <div className="md:col-span-2 space-y-6">
                        {job.description && (
                            <Section title="Role overview">
                                <FormattedText text={job.description} />
                            </Section>
                        )}
                        {job.requirements && (
                            <Section title="What we're looking for">
                                <FormattedText text={job.requirements} />
                            </Section>
                        )}
                    </div>

                    <aside className="md:col-span-1">
                        <div className="bg-white border border-[#E5E5E5] p-5 sticky top-20">
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-3">
                                Fast-track
                            </p>
                            <p className="text-sm text-[#525252] mb-4 leading-relaxed">
                                One profile, apply everywhere. Recruiters can read your resume in under 60 seconds.
                            </p>
                            <button
                                onClick={() => navigate(applyHref)}
                                data-testid="jd-apply-aside"
                                className="btn-gradient w-full px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 mb-2"
                            >
                                Apply for this role
                            </button>
                            <button
                                onClick={() => setShareOpen(true)}
                                data-testid="jd-share-aside"
                                className="w-full border border-[#E5E5E5] hover:border-ink px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
                            >
                                <ShareNetwork size={12} weight="bold" /> Share this JD
                            </button>
                        </div>
                    </aside>
                </div>
            </div>

            <ShareJobModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                jobId={id}
                jobTitle={job.title}
                clientName={job.client?.name}
                refCode={myReferralCode}
                salary={job.salary_range}
                location={job.location}
            />

            {/* V1.6.7 — In-place signup for wishlist saves. */}
            <InlineSignupModal
                open={signupModal}
                onClose={() => setSignupModal(false)}
                contextLine={`So you can save ${job?.title ? `"${job.title}"` : "this role"} to your wishlist and pick it up later.`}
                preservedDraft=""
                onAuthed={async () => {
                    setSignupModal(false);
                    // After AuthContext refreshes, the pending-save effect
                    // fires the actual save.
                }}
            />
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                {title}
            </p>
            <div className="bg-white border border-[#E5E5E5] p-5 text-sm text-[#262626] leading-relaxed">
                {children}
            </div>
        </section>
    );
}

import DOMPurify from "dompurify";

function FormattedText({ text }) {
    // If the JD contains HTML, sanitize with DOMPurify before injecting.
    // Recruiters author the JD, but a compromised or malicious recruiter
    // account should NOT be able to inject <script> or exfiltrating
    // handlers into a public unauthenticated career page (SEC-003, V1.1
    // audit Feb 2026). Non-HTML JDs render as plain text with line
    // breaks preserved.
    if (!text) return null;
    if (/<\/?[a-z][\s\S]*>/i.test(text)) {
        const clean = DOMPurify.sanitize(text, {
            ALLOWED_TAGS: [
                "p", "br", "strong", "em", "u", "b", "i", "ul", "ol", "li",
                "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "code",
                "pre", "hr", "span", "div", "a",
            ],
            ALLOWED_ATTR: ["href", "title", "target", "rel"],
            FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input"],
            ADD_ATTR: ["target"],
        });
        return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: clean }} />;
    }
    return (
        <div className="whitespace-pre-wrap">{text}</div>
    );
}
